import { ChangeDetectionStrategy, Component, computed, inject, model, output, signal } from '@angular/core';
import { TranslocoModule, TranslocoService } from '@ngneat/transloco';
import { SharedModule } from '@shared/shared.module';
import { CONFIRMER } from '@shared/components/dialogs/confirm/types';
import { NOTIFIER } from '@shared/notifier/types';
import { ClipboardService } from '@shared/services/clipboard.service';
import { PlatformChartType } from '@data/_generated-api/platform-api/models/platform-chart-type';
import { HdContentLanguageService } from '../bodygraph/hd-content-language.service';
import { HdChartShareChange, HdChartShareTarget, buildHdChartShareUrl } from './hd-chart-share.models';
import { HdChartShareService } from './hd-chart-share.service';

/**
 * Deeldialoog (spec 2026-10-20-chart-share-analyse §4.1): gedeeld door de chart manager
 * (rij-actie) en de shortcutbalk (actieve chart). Niet gedeeld → uitleg + "Deellink
 * maken"; gedeeld → link met kopieerknop + "Delen stoppen" (confirm). Elke wijziging gaat
 * via `shareChanged` terug naar de host, die zijn eigen lijst/cache bijwerkt.
 */
@Component({
  selector: 'hd-chart-share-dialog',
  standalone: true,
  imports: [SharedModule, TranslocoModule],
  templateUrl: './hd-chart-share-dialog.component.html',
  styleUrl: './hd-chart-share-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HdChartShareDialogComponent {
  private readonly transloco = inject(TranslocoService);
  private readonly contentLang = inject(HdContentLanguageService);
  private readonly shareService = inject(HdChartShareService);
  private readonly clipboard = inject(ClipboardService);
  private readonly confirmer = inject(CONFIRMER);
  private readonly notifier = inject(NOTIFIER);

  /** Te delen chart; null = dialog gesloten. */
  readonly target = model<HdChartShareTarget | null>(null);
  readonly shareChanged = output<HdChartShareChange>();

  protected readonly busy = signal(false);

  protected readonly shareUrl = computed(() => {
    const token = this.target()?.shareToken;
    return token ? buildHdChartShareUrl(token) : null;
  });

  protected readonly text = computed(() => {
    this.contentLang.translationsLoadedTick();
    const language = this.contentLang.effectiveLang();
    const name = this.target()?.name ?? '';
    const type = this.target()?.type ?? PlatformChartType.Standard;
    const translate = (key: string, params?: Record<string, unknown>) =>
      this.transloco.translate<string>(key, params, language);
    // Uitleg per charttype: wat de kijker ziet (geboortegegevens, bij een composiet van
    // beide personen) en of hij de chart kan opslaan (enkel een standaardchart).
    const introKey = type === PlatformChartType.Composite
      ? 'introComposite'
      : type === PlatformChartType.Cycle
        ? 'introCycle'
        : type === PlatformChartType.Transit
          ? 'introMoment'
          : 'intro';
    return {
      title: translate('humanDesign.chartShare.dialog.title'),
      close: translate('actions.close'),
      intro: translate(`humanDesign.chartShare.dialog.${introKey}`, { name }),
      noSaveNote: type === PlatformChartType.Standard ? '' : translate('humanDesign.chartShare.dialog.noSaveNote'),
      nameNote: translate('humanDesign.chartShare.dialog.nameNote'),
      create: translate('humanDesign.chartShare.dialog.create'),
      linkLabel: translate('humanDesign.chartShare.dialog.linkLabel'),
      copy: translate('humanDesign.chartShare.dialog.copy'),
      stop: translate('humanDesign.chartShare.dialog.stop'),
      stopConfirm: translate('humanDesign.chartShare.dialog.stopConfirm', { name }),
      created: translate('humanDesign.chartShare.dialog.created'),
      stopped: translate('humanDesign.chartShare.dialog.stopped'),
      notShared: translate('humanDesign.chartShare.dialog.notShared'),
    };
  });

  protected onOpenChange(open: boolean): void {
    if (!open) this.target.set(null);
  }

  protected async create(): Promise<void> {
    const target = this.target();
    if (!target || this.busy()) return;
    this.busy.set(true);
    try {
      const change = await this.shareService.share(target.id);
      this.target.set({ ...target, shareToken: change.shareToken });
      this.shareChanged.emit(change);
      this.notifier.success(this.text().created);
    } catch (e) {
      this.notifier.error(e instanceof Error ? e.message : `${e}`);
    } finally {
      this.busy.set(false);
    }
  }

  protected copy(): void {
    const url = this.shareUrl();
    if (url) this.clipboard.copyToClipBoard(url);
  }

  protected async stop(): Promise<void> {
    const target = this.target();
    if (!target || this.busy()) return;
    if (!(await this.confirmer.confirm(this.text().stopConfirm, { color: 'danger' }))) return;
    this.busy.set(true);
    try {
      const change = await this.shareService.unshare(target.id);
      this.target.set({ ...target, shareToken: null });
      this.shareChanged.emit(change);
      this.notifier.success(this.text().stopped);
    } catch (e) {
      this.notifier.error(e instanceof Error ? e.message : `${e}`);
    } finally {
      this.busy.set(false);
    }
  }
}
